import { styled } from '@mui/material';
import { useCallback, useContext, useEffect, useMemo } from 'react';

import { AuthContext } from '../../contexts/AuthContext';
import { HeaderContext } from '../../contexts/HeaderContext';
import type { ITimeframe } from '../../core/types/dateTypes';
import { useGlobalStore } from '../../store/store';
import { MarketplaceSelect } from '../../ui-components';
import { ComparativeSelect } from './components/ComparativeSelect';
import { CurrencySelect } from './components/CurrencySelect';
import { DateRangeSelect } from './components/dateRangeSelect/DateRangeSelect';

export const FrontHeader = () => {
  const { marketplaces, setMarketplaces, currency, setCurrency, dates, setDates } =
    useGlobalStore();
  const { apiUser, setVisibleMetrics } = useContext(AuthContext);
  const {
    pageTitle,
    setMarketplace,
    pageMarketplace,
    pageTimeframe = 'timeframe',
    setCurrency: oldSetCurrency,
    setDates: oldSetDates,
    displayTimeFrameInterval,
    pageComparativeSelector,
  } = useContext(HeaderContext);

  useEffect(() => {
    if (marketplaces.length === 1 && marketplaces[0] === 'Walmart') {
      setVisibleMetrics({
        paid: false,
        sns: false,
        traffic: false,
        ntb: false,
      });
    } else {
      setVisibleMetrics(apiUser?.defaultVisibility);
    }
  }, [apiUser?.defaultVisibility, marketplaces, setVisibleMetrics]);

  const range = useMemo(
    () =>
      pageTimeframe === 'snapshot'
        ? {
            startDate: dates.tp_s,
            endDate: dates.tp_e,
            timeframe: dates.timeframe,
          }
        : {
            startDate: dates.tf_s,
            endDate: dates.tf_e,
            timeframe: dates.timeframe,
          },
    [dates.tf_e, dates.tf_s, dates.timeframe, dates.tp_e, dates.tp_s, pageTimeframe],
  );

  const onMarketplacesChange = useCallback(
    (m: string[]) => {
      setMarketplaces(m);
      setMarketplace(m);
    },
    [setMarketplace, setMarketplaces],
  );

  const onCurrencyChange = useCallback(
    (c: { value: string; label: string }) => {
      setCurrency(c.value);
      oldSetCurrency(c.value);
    },
    [oldSetCurrency, setCurrency],
  );

  const onDateChange = useCallback(
    (d: { startDate: Date; endDate: Date; timeframe: ITimeframe }) => {
      setDates(
        pageTimeframe === 'snapshot'
          ? {
              tp_s: d.startDate,
              tp_e: d.endDate,
              timeframe: d.timeframe,
            }
          : {
              tf_s: d.startDate,
              tf_e: d.endDate,
              timeframe: d.timeframe,
            },
      );
      oldSetDates({
        ...dates,
        timeframe: d.timeframe,
        ...(pageTimeframe === 'snapshot'
          ? {
              tp_s: d.startDate,
              tp_e: d.endDate,
            }
          : {
              tf_s: d.startDate,
              tf_e: d.endDate,
            }),
      });
    },
    [dates, pageTimeframe, setDates, oldSetDates],
  );

  return (
    <Wrapper>
      <Title>{pageTitle}</Title>
      <Actions>
        {pageMarketplace && (
          <MarketplaceSelect
            pageMarketplace={pageMarketplace}
            value={marketplaces}
            onChange={onMarketplacesChange}
          />
        )}
        <CurrencySelect currency={currency} onCurrencyChange={onCurrencyChange} />
        {['snapshot', 'timeframe'].includes(pageTimeframe) && (
          <>
            <DateRangeSelect
              range={range}
              onChange={onDateChange}
              displayTimeFrameInterval={displayTimeFrameInterval}
            />
            {pageComparativeSelector && <ComparativeSelect />}
          </>
        )}
      </Actions>
    </Wrapper>
  );
};

const Wrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  padding: '16px 40px 0 40px',
  gap: 8,
  justifyContent: 'space-between',
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const Title = styled('div')(({ theme }) => ({
  fontSize: 24,
  fontFamily: theme.fonts.telkaExtended,
  display: 'block',
  alignItems: 'center',
  fontWeight: 500,
  lineHeight: '32px',
  textWrap: 'nowrap',
  flexGrow: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const Actions = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, auto)',
  gap: 8,
  justifyContent: 'space-between',
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(2, auto)',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(1, auto)',
  },
}));
